import React, { useCallback, useEffect, useState } from "react";
import { get, isEmpty, set } from "lodash";
import T from "prop-types";
import api from "APP_ROOT/api";
import {
  Modal,
  Header,
  Body,
  Footer,
  Close
} from "MODULES/shared/components/Modal";
import { Form as AppForm } from "MODULES/shared/components/Forms";
import {
  AUTO_ADJUST_SRP,
  BUY_AR_FOR_STOCK,
  BUYER_CREDIT_PCT,
  CAN_EXCHANGE,
  DEFAULT_HAZMAT_FEE,
  EXCHANGE_CREDIT_PCT,
  MIN_MANAGED_REPAIR_FEE,
  MINIMUM_EXCHANGE_SRP,
  SETTINGS,
  SHIPPING_OPTIONS,
  RECEIVING_BANK_NAME,
  INTERMEDIARY_BANK,
  ABA_ROUTING,
  ACH_ROUTING,
  SWIFT_CODE,
  BENEFICIARY_ACCOUNT,
  IBAN,
  LABEL_SIZE,
  LABEL_SIZES,
  AS_REMOVED,
  CONDITIONS_WITH_SERVICEABLE,
  MIN_OUTRIGHT_VALUE_FOR_EXCHANGE_CONSIDERATION,
  FREIGHT_COMPLETE_RESTRICTION,
  EXCHANGE_CORE_DUE_DAYS,
  FUZZY_SIMILARITY_PERCENT,
  DEFAULT_NO_FAULT_FOUND_FEE,
  MINIMUM_REPAIR_FEE
} from "APP_ROOT/constants";
import CheckGroup from "MODULES/shared/components/Forms/Check/CheckGroup";
import Check from "MODULES/shared/components/Forms/Check/Check";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import { Col } from "react-bootstrap";
import useDispatch from "APP_ROOT/hooks/useDispatch";
import { BUYER_CREDIT_PCT_OPTIONS } from "MODULES/PurchaseOrders/constants";
import { DEFAULT_CARRIERS } from "MODULES/shared/constants";
import { useCurrentUser } from "APP_ROOT/hooks";
import { SET_SETTINGS } from "MODULES/session/actionTypes";
import { LOCATIONS } from "MODULES/Locations/constants";
import { BEYOND_ECON_REPAIR } from "MODULES/Receiving/constants";
import { removeKeys } from "LIB/removeKey";
import { WARRANTY_OPTIONS } from "MODULES/inventories/constants";
import { NoteTooltip } from "MODULES/shared/components";
import { properCase } from "LIB/formatters/text";

function SettingsModal({
  closed,
  close,
  editing,
  saveSetting,
  locations = [],
  settings = {},
  submitting = false,
  updateLocation,
  updateSetting
}) {
  if (closed) {
    return null;
  }

  return (
    <Modal open={editing} close={close} dialogClass="modal-lg">
      <Header>
        <Close close={close} />
        <h4 className="modal-title">InfoTrader Settings</h4>
      </Header>

      <Body>
        <FormRow>
          <Col md={2}>
            <CheckGroup
              title={[
                "Freight Complete",
                <NoteTooltip
                  note="Do not display commissions in PAY ALL when 'Freight Complete' = No."
                  icon="fa-info-circle"
                  color="#545454"
                />
              ]}
            >
              <Check
                onChange={val =>
                  updateSetting(FREIGHT_COMPLETE_RESTRICTION, val)}
                input={{
                  name: FREIGHT_COMPLETE_RESTRICTION,
                  value: get(settings, FREIGHT_COMPLETE_RESTRICTION),
                  onChange: () => {}
                }}
                label="Yes/No"
              />
            </CheckGroup>
          </Col>
          <Col md={2}>
            <CheckGroup title="Auto Adjust SRP">
              <Check
                onChange={val => updateSetting(AUTO_ADJUST_SRP, val)}
                input={{
                  name: AUTO_ADJUST_SRP,
                  value: get(settings, AUTO_ADJUST_SRP),
                  onChange: () => {}
                }}
                label="Yes/No"
              />
            </CheckGroup>
          </Col>
          <Col md={2}>
            <CheckGroup title="Buy AR For Stock">
              <Check
                onChange={val => updateSetting(BUY_AR_FOR_STOCK, val)}
                input={{
                  name: BUY_AR_FOR_STOCK,
                  value: get(settings, BUY_AR_FOR_STOCK),
                  onChange: () => {}
                }}
                label="Yes/No"
              />
            </CheckGroup>
          </Col>
          <Col md={2}>
            <label className="control-label" htmlFor="default_buyer_credit">
              Default Buyer Credit
            </label>
            <select
              className="form-control"
              name={BUYER_CREDIT_PCT}
              onChange={e => updateSetting(BUYER_CREDIT_PCT, e.target.value)}
              value={get(settings, BUYER_CREDIT_PCT)}
            >
              <option value="">Select Default</option>
              {BUYER_CREDIT_PCT_OPTIONS.map(r => (
                <option key={r.value} value={r.value}>
                  {r.text}
                </option>
              ))}
            </select>
          </Col>
          <Col md={2}>
            <label className="control-label" htmlFor={DEFAULT_HAZMAT_FEE}>
              Default Hazmat Fee
            </label>
            <input
              className="form-control"
              type="number"
              step={0.01}
              value={get(settings, DEFAULT_HAZMAT_FEE)}
              onChange={e => updateSetting(DEFAULT_HAZMAT_FEE, e.target.value)}
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col md={2}>
            <label className="control-label" htmlFor={FUZZY_SIMILARITY_PERCENT}>
              Fuzzy Similarity %
              <NoteTooltip
                note="100% will display only exact matches, blank and zero will disable the feature for restricted entity listings."
                icon="fa-info-circle"
                color="#545454"
              />
            </label>
            <input
              className="form-control"
              type="number"
              step={0.01}
              value={get(settings, FUZZY_SIMILARITY_PERCENT)}
              onChange={e =>
                updateSetting(FUZZY_SIMILARITY_PERCENT, e.target.value)}
            />
          </Col>
          <Col md={3}>
            <label
              className="control-label"
              htmlFor={DEFAULT_NO_FAULT_FOUND_FEE}
            >
              {properCase(DEFAULT_NO_FAULT_FOUND_FEE)}
            </label>
            <input
              className="form-control"
              type="number"
              step={0.01}
              value={get(settings, DEFAULT_NO_FAULT_FOUND_FEE)}
              onChange={e =>
                updateSetting(DEFAULT_NO_FAULT_FOUND_FEE, e.target.value)}
            />
          </Col>
          <Col md={2} mdOffset={1}>
            <label className="control-label" htmlFor={MIN_MANAGED_REPAIR_FEE}>
              Min Managed Repair Fee
            </label>
            <input
              className="form-control"
              type="number"
              step={0.01}
              value={get(settings, MIN_MANAGED_REPAIR_FEE)}
              onChange={e =>
                updateSetting(MIN_MANAGED_REPAIR_FEE, e.target.value)}
            />
          </Col>
          <Col md={2}>
            <label className="control-label" htmlFor={MINIMUM_REPAIR_FEE}>
              Min Repair Admin Fee
            </label>
            <input
              className="form-control"
              type="number"
              step={0.01}
              value={get(settings, MINIMUM_REPAIR_FEE)}
              onChange={e => updateSetting(MINIMUM_REPAIR_FEE, e.target.value)}
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col md={12}>
            <h5>Exchange Sales</h5>
          </Col>
        </FormRow>
        <FormRow>
          <Col md={2}>
            <CheckGroup title="Can Exchange">
              <Check
                onChange={val => updateSetting(CAN_EXCHANGE, val)}
                input={{
                  name: CAN_EXCHANGE,
                  value: get(settings, CAN_EXCHANGE),
                  onChange: () => {}
                }}
                label="Yes/No"
              />
            </CheckGroup>
          </Col>
          <Col md={2}>
            <label className="control-label" htmlFor={MINIMUM_EXCHANGE_SRP}>
              Min Exchange SRP
            </label>
            <input
              className="form-control"
              type="number"
              step={0.01}
              value={get(settings, MINIMUM_EXCHANGE_SRP)}
              onChange={e =>
                updateSetting(MINIMUM_EXCHANGE_SRP, e.target.value)}
            />
          </Col>
          <Col md={3}>
            <label className="control-label" htmlFor={EXCHANGE_CREDIT_PCT}>
              Cost % Applied to Exch. Transactions
            </label>
            <input
              className="form-control"
              type="number"
              step={0.01}
              value={get(settings, EXCHANGE_CREDIT_PCT)}
              onChange={e => {
                if (
                  (e.target.value >= 0 && e.target.value <= 100) ||
                  isEmpty(e.target.value)
                ) {
                  updateSetting(EXCHANGE_CREDIT_PCT, e.target.value);
                }
              }}
            />
          </Col>
          <Col md={2}>
            <label className="control-label" htmlFor={EXCHANGE_CORE_DUE_DAYS}>
              Exchange Core Due Days
            </label>
            <input
              className="form-control"
              type="number"
              step={0.01}
              value={get(settings, EXCHANGE_CORE_DUE_DAYS)}
              onChange={e => {
                updateSetting(EXCHANGE_CORE_DUE_DAYS, e.target.value);
              }}
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col md={4}>
            <label
              className="control-label"
              htmlFor={MIN_OUTRIGHT_VALUE_FOR_EXCHANGE_CONSIDERATION}
            >
              Min Outright Value for Exch Consideration
            </label>
            <input
              className="form-control"
              type="number"
              step={0.01}
              value={get(
                settings,
                MIN_OUTRIGHT_VALUE_FOR_EXCHANGE_CONSIDERATION
              )}
              onChange={e => {
                updateSetting(
                  MIN_OUTRIGHT_VALUE_FOR_EXCHANGE_CONSIDERATION,
                  e.target.value
                );
              }}
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col md={3}>
            <h5>Shipping Details</h5>
          </Col>
        </FormRow>
        <FormRow>
          {DEFAULT_CARRIERS.map(c => {
            const path = `${SHIPPING_OPTIONS}.${c}.default_account`;
            return (
              <Col md={3}>
                <label className="control-label" htmlFor="default_account">
                  {`${c.toUpperCase()} Default Account`}
                </label>
                <input
                  className="form-control"
                  onChange={e => updateSetting(path, e.target.value)}
                  value={get(settings, path)}
                />
              </Col>
            );
          })}
        </FormRow>
        <FormRow>
          <Col md={3}>
            <h5>Resale Tax #</h5>
          </Col>
        </FormRow>
        <FormRow>
          {locations.map((l, i) => {
            const path = `[${i}].resale_number`;
            return (
              <Col md={3}>
                <label className="control-label" htmlFor="default_account">
                  {`${l.name.toUpperCase()} Resale Tax #`}
                </label>
                <input
                  className="form-control"
                  onChange={e => updateLocation(path, e.target.value)}
                  value={get(locations, path)}
                />
              </Col>
            );
          })}
        </FormRow>
        <FormRow>
          <Col md={3}>
            <h5>Inventory Label Paper Size</h5>
          </Col>
        </FormRow>
        <FormRow>
          <Col md={4}>
            <label className="control-label" htmlFor="cancellation_reason">
              Inventory Label Paper Size
            </label>
            <select
              className="form-control"
              name={LABEL_SIZE}
              onChange={e => updateSetting(LABEL_SIZE, e.target.value)}
              value={get(settings, LABEL_SIZE)}
            >
              <option value="">Select Default</option>
              {LABEL_SIZES.map(r => (
                <option key={r.value} value={r.value}>
                  {r.text}
                </option>
              ))}
            </select>
          </Col>
        </FormRow>
        {locations.map(l => (
          <React.Fragment>
            <FormRow>
              <Col md={3}>
                <h5>Bank Details for {l.name}</h5>
              </Col>
            </FormRow>
            <FormRow>
              <Col md={2}>
                <label className="control-label" htmlFor={RECEIVING_BANK_NAME}>
                  Receiving Bank Name
                </label>
                <input
                  className="form-control"
                  type="text"
                  onChange={e =>
                    updateSetting(
                      `stores.${l.id}.${RECEIVING_BANK_NAME}`,
                      e.target.value
                    )}
                  value={get(settings, `stores.${l.id}.${RECEIVING_BANK_NAME}`)}
                />
              </Col>
              <Col md={2}>
                <label className="control-label" htmlFor={INTERMEDIARY_BANK}>
                  Intermediary Bank
                </label>
                <input
                  className="form-control"
                  type="text"
                  onChange={e =>
                    updateSetting(
                      `stores.${l.id}.${INTERMEDIARY_BANK}`,
                      e.target.value
                    )}
                  value={get(settings, `stores.${l.id}.${INTERMEDIARY_BANK}`)}
                />
              </Col>
              <Col md={2}>
                <label className="control-label" htmlFor={SWIFT_CODE}>
                  Swift Code
                </label>
                <input
                  className="form-control"
                  type="text"
                  onChange={e =>
                    updateSetting(
                      `stores.${l.id}.${SWIFT_CODE}`,
                      e.target.value
                    )}
                  value={get(settings, `stores.${l.id}.${SWIFT_CODE}`)}
                />
              </Col>
              <Col md={2}>
                <label className="control-label" htmlFor={IBAN}>
                  IBAN#
                </label>
                <input
                  className="form-control"
                  type="text"
                  onChange={e =>
                    updateSetting(`stores.${l.id}.${IBAN}`, e.target.value)}
                  value={get(settings, `stores.${l.id}.${IBAN}`)}
                />
              </Col>
            </FormRow>
            <FormRow>
              <Col md={2}>
                <label className="control-label" htmlFor={ABA_ROUTING}>
                  ABA / Routing#
                </label>
                <input
                  className="form-control"
                  type="text"
                  onChange={e =>
                    updateSetting(
                      `stores.${l.id}.${ABA_ROUTING}`,
                      e.target.value
                    )}
                  value={get(settings, `stores.${l.id}.${ABA_ROUTING}`)}
                />
              </Col>
              <Col md={2}>
                <label className="control-label" htmlFor={ACH_ROUTING}>
                  ACH / Routing#
                </label>
                <input
                  className="form-control"
                  type="text"
                  onChange={e =>
                    updateSetting(
                      `stores.${l.id}.${ACH_ROUTING}`,
                      e.target.value
                    )}
                  value={get(settings, `stores.${l.id}.${ACH_ROUTING}`)}
                />
              </Col>
              <Col md={2}>
                <label className="control-label" htmlFor={BENEFICIARY_ACCOUNT}>
                  Beneficiary Account
                </label>
                <input
                  className="form-control"
                  type="text"
                  onChange={e =>
                    updateSetting(
                      `stores.${l.id}.${BENEFICIARY_ACCOUNT}`,
                      e.target.value
                    )}
                  value={get(settings, `stores.${l.id}.${BENEFICIARY_ACCOUNT}`)}
                />
              </Col>
            </FormRow>
          </React.Fragment>
        ))}
        <FormRow>
          <Col md={3}>
            <h5>Warranty Period</h5>
          </Col>
        </FormRow>
        <FormRow>
          <Col md={6}>
            {Object.keys(
              removeKeys(CONDITIONS_WITH_SERVICEABLE, [
                AS_REMOVED,
                BEYOND_ECON_REPAIR
              ])
            ).map(c => (
              <Col md={12}>
                <label
                  className="control-label"
                  htmlFor={CONDITIONS_WITH_SERVICEABLE[c].label}
                >
                  {CONDITIONS_WITH_SERVICEABLE[c].label}
                </label>
                <select
                  className="form-control"
                  value={get(
                    settings,
                    `${c}_warranty`,
                    CONDITIONS_WITH_SERVICEABLE[c].default_warranty
                  )}
                  onChange={e => updateSetting(`${c}_warranty`, e.target.value)}
                >
                  <option value="">Select Default</option>
                  {WARRANTY_OPTIONS.map(f => <option value={f}>{f}</option>)}
                </select>
              </Col>
            ))}
          </Col>
          <Col md={6}>
            <label htmlFor="tag_date_months_for_warranty">
              For Tag Dates older than (IN MONTHS)&nbsp;
            </label>
            <input
              className="form-control"
              style={{ width: 100 }}
              type="number"
              value={get(settings, "tag_date_months_for_warranty")}
              onChange={e =>
                updateSetting(`tag_date_months_for_warranty`, e.target.value)}
            />
            <br />
            <label htmlFor="tag_date_warranty">Warranty limited to&nbsp;</label>
            <select
              className="form-control"
              value={get(settings, `tag_date_warranty`)}
              onChange={e => updateSetting(`tag_date_warranty`, e.target.value)}
            >
              <option value="">Select Default</option>
              {WARRANTY_OPTIONS.map(f => <option value={f}>{f}</option>)}
            </select>
          </Col>
        </FormRow>
      </Body>

      <Footer>
        <AppForm.Cancel close={close} editable />
        <AppForm.Submit
          disabled={false}
          editable
          submitting={submitting}
          onClick={() => {
            saveSetting();
            close();
          }}
        />
      </Footer>
    </Modal>
  );
}

SettingsModal.propTypes = {
  editing: T.bool,
  closed: T.bool,
  close: T.func.isRequired,
  locations: T.arrayOf(T.shape({})),
  saveSetting: T.func.isRequired,
  settings: T.shape({}),
  submitting: T.bool.isRequired,
  updateLocation: T.func.isRequired,
  updateSetting: T.func.isRequired
};

export default function Settings() {
  const currentUser = useCurrentUser();
  const [locations, setLocations] = useState(
    currentUser.locations.map(l => ({
      id: l.id,
      name: l.name,
      resale_number: l.resale_number
    }))
  );
  const [settings, setSetting] = useState({});
  const [closed, toggle] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [changes, setChanges] = useState(0);
  const dispatch = useDispatch();

  useEffect(() => {
    api.appSetting.index().then(({ data }) => {
      setSetting(get(data, SETTINGS) || {});
      return function cleanup() {};
    });
  }, []);

  const saveSetting = useCallback(
    () => {
      setSubmitting(true);
      api.appSetting.update({ locations, settings }).then(({ data: resp }) => {
        setSubmitting(false);
        setSetting(get(resp, SETTINGS));
        dispatch({
          type: SET_SETTINGS,
          settings: get(resp, SETTINGS),
          locations: get(resp, LOCATIONS)
        });
      });
    },
    [locations, settings, dispatch]
  );

  const updateSetting = useCallback(
    (path, value) => {
      setSetting(set(settings, path, value));
      setChanges(changes + 1);
    },
    [settings, changes]
  );

  const updateLocation = useCallback(
    (path, value) => {
      setLocations(set(locations, path, value));
      setChanges(changes + 1);
    },
    [locations, changes]
  );

  return (
    <React.Fragment>
      <a style={{ cursor: "pointer" }} onClick={() => toggle(!closed)}>
        InfoTrader Settings
      </a>
      <SettingsModal
        editing={!closed}
        close={() => toggle(true)}
        locations={locations}
        settings={settings}
        saveSetting={saveSetting}
        submitting={submitting}
        updateLocation={updateLocation}
        updateSetting={updateSetting}
        changes={changes}
      />
    </React.Fragment>
  );
}



// WEBPACK FOOTER //
// ./app/modules/Admin/Settings/Settings.jsx